import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

/**
 * Simple accessible FAQ accordion — renders visible Q&A pairs so Google
 * can index the content that pairs with the FAQPage JSON-LD in the head.
 * Without visible content, JSON-LD FAQ signals get ignored by search engines.
 */
export default function FaqSection({
  eyebrow = 'Frequently Asked',
  title = 'Questions we get a lot.',
  subtitle = null,
  items = [],
  testId = 'faq-section',
}) {
  const [open, setOpen] = useState(0);
  if (!items || items.length === 0) return null;

  return (
    <section className="py-16 lg:py-24 bg-[#fafafa] border-t border-black/10" data-testid={testId}>
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#e11d2c]">
          <HelpCircle size={13} /> {eyebrow}
        </div>
        <h2 className="font-display mt-3 text-[32px] sm:text-[42px] font-black tracking-tight">{title}</h2>
        {subtitle && <p className="mt-3 text-black/60 text-[15px] max-w-2xl">{subtitle}</p>}

        <div className="mt-8 divide-y divide-black/10 bg-white border border-black/10 rounded-2xl overflow-hidden">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div key={i} data-testid={`${testId}-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 lg:px-6 py-4 lg:py-5 hover:bg-black/[0.02] transition-colors"
                  data-testid={`${testId}-question-${i}`}
                >
                  <span className="font-display text-[15px] lg:text-[17px] font-bold text-black">{it.q}</span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 text-black/50 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#e11d2c]' : ''}`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 lg:px-6 pb-5 lg:pb-6 -mt-1 text-[14px] lg:text-[15px] text-black/70 leading-relaxed" data-testid={`${testId}-answer-${i}`}>
                    {it.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
